if (!customElements.get('product-info-drawer')) {
  customElements.define(
    'product-info-drawer',
    class ProductInfoDrawer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.panel = this.querySelector('.rtd-product-info-drawer__panel');
        this.closeButton = this.querySelector('[data-product-info-drawer-close]');
        this.currentVariantId = this.dataset.currentVariantId || '';

        this.onDrawerClick = this.onDrawerClick.bind(this);
        this.onDrawerKeyup = this.onDrawerKeyup.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onOpenRequest = this.onOpenRequest.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onEditorSelect = this.onEditorSelect.bind(this);
        this.onEditorDeselect = this.onEditorDeselect.bind(this);

        this.addEventListener('click', this.onDrawerClick);
        this.addEventListener('keyup', this.onDrawerKeyup);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('rtd:product-info-drawer:open', this.onOpenRequest);
        document.addEventListener('rtd:variant-change', this.onVariantChange);

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:section:select', this.onEditorSelect);
          document.addEventListener('shopify:block:select', this.onEditorSelect);
          document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
        }

        const selectedVariantId = document.querySelector('rtd-in-person-course')?.dataset.selectedVariantId;
        if (selectedVariantId && selectedVariantId !== this.currentVariantId) {
          this.updateForVariant(selectedVariantId);
        }
      }

      disconnectedCallback() {
        this.close({ restoreFocus: false });
        this.removeEventListener('click', this.onDrawerClick);
        this.removeEventListener('keyup', this.onDrawerKeyup);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('rtd:product-info-drawer:open', this.onOpenRequest);
        document.removeEventListener('rtd:variant-change', this.onVariantChange);
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        this.variantRequest?.abort();
        this.initialized = false;
      }

      onDrawerClick(event) {
        if (event.target.closest('[data-product-info-drawer-close], [data-product-info-drawer-overlay]')) {
          this.close();
          return;
        }

        const continueButton = event.target.closest('[data-product-info-drawer-continue]');
        if (continueButton && this.contains(continueButton)) {
          event.preventDefault();
          this.continueToCart();
          return;
        }

        const toggle = event.target.closest('[data-product-info-drawer-item-toggle]');
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          this.toggleItem(toggle);
          return;
        }

        const header = event.target.closest('[data-product-info-drawer-item-header]');
        if (!header || !this.contains(header)) return;
        if (
          event.target.closest(
            '.rtd-product-info-drawer__item-purchase, .rtd-product-info-drawer__item-image, .rtd-product-info-drawer__item-image-placeholder',
          )
        )
          return;

        const headerToggle = header.querySelector('[data-product-info-drawer-item-toggle]');
        if (!headerToggle) return;

        event.preventDefault();
        this.toggleItem(headerToggle);
      }

      toggleItem(toggle) {
        const bodyId = toggle.getAttribute('aria-controls');
        const body = bodyId ? document.getElementById(bodyId) : null;
        if (!body || !this.contains(body)) return;

        const nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(nextExpanded));
        if (toggle.matches('[data-product-info-drawer-read-more]')) {
          const item = toggle.closest('.rtd-product-info-drawer__item');
          const preview = item?.querySelector('[data-product-info-drawer-item-preview]');
          const collapsedLabel = toggle.dataset.collapsedLabel || 'Read more';
          const expandedLabel = toggle.dataset.expandedLabel || 'Read less';
          const heading = item?.querySelector('.rtd-product-info-drawer__item-heading')?.textContent?.trim();
          const visibleLabel = nextExpanded ? expandedLabel : collapsedLabel;

          toggle.textContent = visibleLabel;
          toggle.setAttribute('aria-label', heading ? `${visibleLabel} about ${heading}` : visibleLabel);
          if (preview) preview.hidden = nextExpanded;
        } else {
          toggle.setAttribute('aria-label', nextExpanded ? 'Hide product details' : 'Show product details');
        }
        body.hidden = !nextExpanded;
      }

      continueToCart() {
        const restoreTarget =
          this.activeElement && this.activeElement.isConnected && !this.contains(this.activeElement)
            ? this.activeElement
            : null;

        this.close({ restoreFocus: false });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open(restoreTarget || undefined);
          return;
        }

        const cartUrl = window.routes?.cart_url || '/cart';
        window.location.assign(cartUrl);
      }

      onDrawerKeyup(event) {
        if (event.code === 'Escape') this.close();
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-product-info-drawer-open]');
        if (!trigger) return;

        const targetSectionId = trigger.dataset.productInfoDrawerOpen;
        const drawers = Array.from(document.querySelectorAll('product-info-drawer'));
        const targetDrawer = targetSectionId
          ? drawers.find((drawer) => drawer.dataset.sectionId === targetSectionId)
          : drawers.find((drawer) => drawer.dataset.hasContent === 'true') || drawers[0];
        if (targetDrawer !== this) return;

        event.preventDefault();
        this.open(trigger);
      }

      async onOpenRequest(event) {
        const { sectionId, trigger, variantId } = event.detail || {};
        if (sectionId && sectionId !== this.dataset.sectionId) return;
        if (variantId && variantId !== this.currentVariantId) {
          await this.updateForVariant(variantId);
        }
        this.open(trigger);
      }

      onVariantChange(event) {
        const variantId = event.detail?.variantId;
        if (variantId && variantId !== this.currentVariantId) this.updateForVariant(variantId);
      }

      onEditorSelect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open(null, { manageFocus: false });
      }

      onEditorDeselect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close({ restoreFocus: false });
      }

      open(triggeredBy, { manageFocus = !window.Shopify?.designMode } = {}) {
        if (this.dataset.hasContent !== 'true' && !window.Shopify?.designMode) return false;
        if (this.getAttribute('aria-hidden') === 'false') return true;

        document.querySelector('cart-drawer.active')?.close();
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification?.notification?.classList.contains('active')) cartNotification.close();
        document
          .querySelectorAll('product-info-drawer[aria-hidden="false"]')
          .forEach((drawer) => drawer !== this && drawer.close({ restoreFocus: false }));

        this.activeElement = triggeredBy || document.activeElement;
        this.setAttribute('aria-hidden', 'false');

        if (manageFocus) {
          document.body.classList.add('overflow-hidden');
          requestAnimationFrame(() => {
            trapFocus(this.panel, this.closeButton || this.panel);
            this.focusTrapped = true;
          });
        }
        return true;
      }

      close({ restoreFocus = true } = {}) {
        if (this.getAttribute('aria-hidden') !== 'false') return;

        this.setAttribute('aria-hidden', 'true');
        if (this.focusTrapped) {
          removeTrapFocus(restoreFocus ? this.activeElement : null);
          this.focusTrapped = false;
        } else if (restoreFocus) {
          this.activeElement?.focus();
        }

        if (!document.querySelector('cart-drawer.active')) {
          document.body.classList.remove('overflow-hidden');
        }
      }

      updateForVariant(variantId) {
        if (this.dataset.variantDependent !== 'true') {
          this.currentVariantId = variantId;
          this.dataset.currentVariantId = variantId;
          return Promise.resolve();
        }
        if (variantId === this.pendingVariantId && this.variantUpdatePromise) return this.variantUpdatePromise;

        this.variantRequest?.abort();
        const request = new AbortController();
        this.variantRequest = request;
        this.pendingVariantId = variantId;

        const updatePromise = (async () => {
          try {
            const url = new URL(this.dataset.productUrl, window.location.origin);
            url.searchParams.set('variant', variantId);
            url.searchParams.set('section_id', this.dataset.sectionId);

            const response = await fetch(url.toString(), { signal: request.signal });
            if (!response.ok) throw new Error(`Product info drawer request failed (${response.status})`);

            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextDrawer = html.querySelector('product-info-drawer');
            const nextContent = nextDrawer?.querySelector('[data-product-info-drawer-content]');
            const currentContent = this.querySelector('[data-product-info-drawer-content]');
            if (!nextDrawer || !nextContent || !currentContent) {
              throw new Error('Product info drawer content is missing');
            }

            if (currentContent.contains(document.activeElement)) this.closeButton?.focus();
            currentContent.innerHTML = nextContent.innerHTML;
            this.dataset.hasContent = nextDrawer.dataset.hasContent;
            this.dataset.currentVariantId = nextDrawer.dataset.currentVariantId;
            this.currentVariantId = nextDrawer.dataset.currentVariantId || variantId;
          } catch (error) {
            if (error.name !== 'AbortError') console.error(error);
          } finally {
            if (this.variantRequest === request) {
              this.variantRequest = null;
              this.pendingVariantId = null;
              this.variantUpdatePromise = null;
            }
          }
        })();

        this.variantUpdatePromise = updatePromise;
        return updatePromise;
      }
    }
  );
}
